import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Trash2, Plus, FileText } from "lucide-react";
import { fmtINR } from "@/lib/format";
import { toast } from "sonner";

const empty = {
  code: "", name: "", series_prefix: "", next_number: 1, padding: 4,
  approval_required: false, approval_threshold: 0, department_id: "", is_active: true,
};

export default function DocumentTypesTab() {
  const [items, setItems] = useState([]);
  const [depts, setDepts] = useState([]);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setItems((await api.get("/document-types")).data || []);
    try { setDepts((await api.get("/departments")).data || []); } catch { setDepts([]); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.code.trim() || !form.name.trim()) return toast.error("Code + Name required");
    const payload = { ...form, department_id: form.department_id || null };
    try {
      if (editing) await api.put(`/document-types/${editing}`, payload);
      else await api.post("/document-types", payload);
      toast.success(editing ? "Updated" : "Document type added");
      setForm(empty); setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this document type?")) return;
    try { await api.delete(`/document-types/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const startEdit = (d) => {
    setEditing(d.id);
    setForm({ ...empty, ...d, department_id: d.department_id || "" });
  };
  const cancel = () => { setEditing(null); setForm(empty); };

  const preview = () => {
    const n = String(form.next_number).padStart(form.padding, "0");
    return (form.series_prefix || "") + n;
  };

  return (
    <div className="space-y-4" data-testid="document-types-tab">
      <div className="text-[10px] font-mono uppercase text-muted-foreground">// Document types · numbering · approval mapping</div>

      <Card className="rounded-sm p-4">
        <div className="text-sm font-semibold mb-3 flex items-center gap-2"><FileText className="w-4 h-4 text-accent" />{editing ? "Edit document type" : "Add document type"}</div>
        <div className="grid md:grid-cols-4 gap-3">
          <div>
            <Label className="text-[10px] uppercase font-mono">Code *</Label>
            <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                   placeholder="INV / PO / JV" className="rounded-sm mt-1 font-mono" data-testid="doctype-code" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-[10px] uppercase font-mono">Name *</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                   placeholder="Sales Invoice" className="rounded-sm mt-1" data-testid="doctype-name" />
          </div>
          <div>
            <Label className="text-[10px] uppercase font-mono">Department</Label>
            <Select value={form.department_id || "__none__"} onValueChange={(v) => setForm({ ...form, department_id: v === "__none__" ? "" : v })}>
              <SelectTrigger className="rounded-sm mt-1" data-testid="doctype-dept"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">—</SelectItem>
                {depts.map(d => <SelectItem key={d.id} value={d.id}>{d.code} · {d.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="mt-3 grid md:grid-cols-4 gap-3">
          <div>
            <Label className="text-[10px] uppercase font-mono">Series prefix</Label>
            <Input value={form.series_prefix} onChange={(e) => setForm({ ...form, series_prefix: e.target.value })}
                   placeholder="INV/2025-26/" className="rounded-sm mt-1 font-mono" data-testid="doctype-prefix" />
          </div>
          <div>
            <Label className="text-[10px] uppercase font-mono">Next number</Label>
            <Input type="number" min={1} value={form.next_number}
                   onChange={(e) => setForm({ ...form, next_number: Number(e.target.value) })}
                   className="rounded-sm mt-1 tabular-nums" data-testid="doctype-next" />
          </div>
          <div>
            <Label className="text-[10px] uppercase font-mono">Padding</Label>
            <Input type="number" min={1} max={10} value={form.padding}
                   onChange={(e) => setForm({ ...form, padding: Number(e.target.value) })}
                   className="rounded-sm mt-1 tabular-nums" data-testid="doctype-padding" />
          </div>
          <div>
            <Label className="text-[10px] uppercase font-mono">Preview</Label>
            <div className="rounded-sm mt-1 h-9 flex items-center px-3 text-xs font-mono border border-accent/40 bg-accent/5" data-testid="doctype-preview">
              {preview()}
            </div>
          </div>
        </div>
        <div className="mt-3 grid md:grid-cols-3 gap-3 items-end">
          <div className="flex items-center justify-between border rounded-sm px-3 py-2">
            <Label>Approval required</Label>
            <Switch checked={form.approval_required} onCheckedChange={(v) => setForm({ ...form, approval_required: v })} data-testid="doctype-approval-req" />
          </div>
          {form.approval_required && (
            <div>
              <Label className="text-[10px] uppercase font-mono">Approval threshold ₹</Label>
              <Input type="number" value={form.approval_threshold}
                     onChange={(e) => setForm({ ...form, approval_threshold: Number(e.target.value) })}
                     className="rounded-sm mt-1 tabular-nums" data-testid="doctype-threshold" />
            </div>
          )}
        </div>
        <div className="mt-3 flex gap-2">
          <Button onClick={save} className="rounded-sm gap-1 bg-accent hover:bg-accent/90 text-white" data-testid="doctype-save">
            <Plus className="w-3.5 h-3.5" /> {editing ? "Update" : "Add"}
          </Button>
          {editing && <Button variant="outline" onClick={cancel} className="rounded-sm" data-testid="doctype-cancel">Cancel</Button>}
        </div>
      </Card>

      <Card className="rounded-sm overflow-hidden">
        <table className="w-full dense">
          <thead><tr><th>Code</th><th>Name</th><th>Series preview</th><th>Department</th><th>Approval</th><th></th></tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan="6" className="text-center py-8 text-muted-foreground">No document types yet — add INV, PO, JV, RCPT, PAY.</td></tr>}
            {items.map((d) => (
              <tr key={d.id} data-testid={`doctype-row-${d.code}`} className={editing === d.id ? "bg-accent/5" : ""}>
                <td className="font-mono text-xs">{d.code}</td>
                <td>{d.name}</td>
                <td className="font-mono text-xs text-muted-foreground">{(d.series_prefix || "") + String(d.next_number).padStart(d.padding || 4, "0")}</td>
                <td className="text-xs">{(depts.find(x => x.id === d.department_id) || {}).code || "—"}</td>
                <td className="text-xs">{d.approval_required ? <span className="text-amber-700 font-mono">≥ {fmtINR(d.approval_threshold)}</span> : <span className="text-muted-foreground">—</span>}</td>
                <td className="text-right space-x-2">
                  <button onClick={() => startEdit(d)} className="text-xs text-accent hover:underline" data-testid={`doctype-edit-${d.code}`}>Edit</button>
                  <button onClick={() => del(d.id)} className="text-muted-foreground hover:text-destructive" data-testid={`doctype-delete-${d.code}`}>
                    <Trash2 className="w-3.5 h-3.5 inline" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
